import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import type { ConversationMessage } from "@/types";
import MessageBubble from "./MessageBubble";
import ToolCallCard from "./ToolCallCard";
import ConfirmationCard from "./ConfirmationCard";
import ThinkingBubble from "./ThinkingBubble";

interface MessageListProps {
  messages: ConversationMessage[];
  onApprove: (invocationId: string, params: Record<string, unknown>) => void;
  onReject: (invocationId: string) => void;
  busyInvocationId?: string | null;
  pending?: boolean;
}

/**
 * Renders the message stream. The orchestrator emits assistant tool-call
 * messages BEFORE the matching tool-result message; we pair them here so
 * the UI shows one card per call (with the result inside) rather than
 * two stacked rows.
 *
 * If the assistant's tool-call has status='pending', we render a
 * ConfirmationCard instead of a ToolCallCard.
 */
export default function MessageList({
  messages,
  onApprove,
  onReject,
  busyInvocationId,
  pending,
}: MessageListProps) {
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, pending]);

  const rendered: React.ReactNode[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];

    if (m.role === "tool") {
      // Tool result already paired with its assistant call below.
      continue;
    }

    if (m.role === "assistant" && m.tool_name && m.tool_invocation_id) {
      // Find the matching tool-result message that follows
      const result = messages
        .slice(i + 1)
        .find(
          (other) => other.role === "tool" && other.tool_invocation_id === m.tool_invocation_id,
        );

      if (m.tool_invocation_status === "pending") {
        rendered.push(
          <ConfirmationCard
            key={m.id}
            message={m}
            onApprove={() =>
              onApprove(
                m.tool_invocation_id as string,
                (m.tool_arguments ?? {}) as Record<string, unknown>,
              )
            }
            onReject={() => onReject(m.tool_invocation_id as string)}
            busy={busyInvocationId === m.tool_invocation_id}
          />,
        );
      } else {
        rendered.push(<ToolCallCard key={m.id} message={m} result={result} />);
      }
      // If the assistant ALSO included natural-language content alongside
      // the call, render it as a bubble too.
      if (m.content && m.content.trim()) {
        rendered.push(<MessageBubble key={`${m.id}-text`} message={m} />);
      }
    } else {
      rendered.push(<MessageBubble key={m.id} message={m} />);
    }
  }

  return (
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2, minWidth: 0 }}>
      {rendered}
      {pending && <ThinkingBubble />}
      <div ref={endRef} />
    </Box>
  );
}
